/* Build profile lists (Apps & profiles panel, Getting started table, archive dialog): pure derivations of what each row shows. */
import type { Project } from '@hangar/shared';
import type { Profile, ProviderAccount } from '@/api/schemas';

type Binding = Profile['environments'][number];
type ProjectEnvironment = Project['environments'][number];

/** Bitrise profiles name a workflow per environment slug (`providerConfig.workflowByEnvironment`). */
export function workflowsOf(p: Profile): Record<string, string> {
  const map = p.providerConfig.workflowByEnvironment;
  if (typeof map !== 'object' || map === null || Array.isArray(map)) return {};
  return Object.fromEntries(Object.entries(map).filter((e): e is [string, string] => typeof e[1] === 'string'));
}

/** What the provider builds: the eas.json profile, or the Bitrise workflows. */
export function buildTarget(p: Profile): string | undefined {
  if (typeof p.providerConfig.profile === 'string') return p.providerConfig.profile;
  return [...new Set(Object.values(workflowsOf(p)))].join(', ') || undefined;
}

/** The profile a binding overrides (`providerConfig.profile`), if it names one. */
export const bindingProfile = (b: Binding): string | undefined => (typeof b.providerConfig?.profile === 'string' ? b.providerConfig.profile : undefined);

/** The override a binding shows: its own profile, else the Bitrise workflow of the environment. */
export function bindingOverride(p: Profile, b: Binding, env: ProjectEnvironment | undefined): string | undefined {
  return bindingProfile(b) ?? (env ? workflowsOf(p)[env.slug] : undefined);
}

/** The environment of a binding as the project knows it. */
export const bindingEnvironment = (project: Project, b: Binding): ProjectEnvironment | undefined => project.environments.find((e) => e.id === b.environmentId);

/** Slug of the bound environment; falls back to the name the API sent, then a short id. */
export const bindingSlug = (project: Project, b: Binding): string => bindingEnvironment(project, b)?.slug ?? b.environment ?? b.environmentId.slice(0, 8);

/** Name of an environment by id (short id when the project does not list it). */
export const environmentName = (project: Project, id: string): string => project.environments.find((e) => e.id === id)?.name ?? id.slice(0, 8);

/** "Staging → store": the environment name and the eas profile its binding overrides. */
export function bindingText(project: Project, b: Binding): string {
  const profile = bindingProfile(b);
  return `${environmentName(project, b.environmentId)}${profile ? ` → ${profile}` : ''}`;
}

/** The phone card's environments line. */
export const bindingsLine = (p: Profile, project: Project): string => (p.environments.length ? p.environments.map((b) => bindingText(project, b)).join(' · ') : 'no environment bindings');

/** How builds of the profile can start, for the subtitle of the row: Run build first (the CLI path is docs/mvp-plan.md P17). */
export function executionLabel(p: Profile): string {
  if (p.executionModes.includes('hangar_runner') || p.executionModes.includes('provider_api')) return 'Run build';
  return p.executionModes.includes('ci_client') ? 'CLI only' : 'cannot build yet';
}

/** The provider account kind of a profile: the one embedded in it, else looked up, else EAS. */
export function profileProviderKind(p: Profile, accounts: ProviderAccount[] | undefined): ProviderAccount['kind'] {
  return p.providerAccount?.kind ?? accounts?.find((a) => a.id === p.providerAccountId)?.kind ?? 'eas';
}

/** "store → production · aab · 120 min": the monospace subtitle of a profile row. */
export function profileSubtitle(p: Profile): string {
  const profile = typeof p.providerConfig.profile === 'string' ? ` → ${p.providerConfig.profile}` : '';
  return `${p.slug}${profile} · ${p.artifactKind} · ${p.timeoutMinutes} min`;
}

const ENV_KIND_TONE: Record<string, string> = { production: 'text-env-production', staging: 'text-env-staging', preprod: 'text-env-preprod' };

/** Text colour of an environment tag by kind (every kind except dev has one). */
export const environmentTone = (kind: string | undefined): string | undefined => (kind ? ENV_KIND_TONE[kind] : undefined);

/** Slugs of the environments a profile builds for, as the archive dialog lists them. */
export const archiveEnvironments = (profile: Profile | null, project: Project): string[] => (profile ? profile.environments.map((b) => bindingSlug(project, b)) : []);

export const platformLabel = (profile: Pick<Profile, 'platform'> | null): string => (profile?.platform === 'ios' ? 'iOS' : 'Android');

/** " (staging, production)" or nothing. */
export const environmentsSuffix = (envs: string[]): string => (envs.length ? ` (${envs.join(', ')})` : '');
